import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import ExpandBtn from '../ExpandBtn/ExpandBtn';
import { countFor100, getDataForPeriodDashboard } from '../../helpers/helpers';
import './DashboardTable.scss';
import FilterForm from '../FilterCommon/FilterForm';
import DropdownDisplayOptions from '../DropdownDisplayOptions/DropdownDisplayOptions';
import * as constants from '../../data/constants';

const DashboardTable = React.memo(
  ({
    responseData,
    responseDataWorld,
    currentCountry,
    isFor100,
    isLastDay,
    selectCountry,
    changeSelectedPeriod,
    changeIsFor100,
    changeShowingData,
  }) => {
    const [isFullScreenSize, setIsFullScreenSize] = useState(false);
    const { i18n, t } = useTranslation();

    const getTotalPopulation = useCallback(
      (country) => {
        if (country) {
          return responseData.find((el) => el.countryInfo.iso3 === country).population;
        }
        return responseDataWorld.population;
      },
      [responseDataWorld, responseData],
    );

    const getTableData = useCallback(
      (country, data) => {
        if (country) {
          return data.find((el) => el.countryInfo.iso3 === country);
        }
        return responseDataWorld;
      },
      [responseDataWorld],
    );

    const HEADINGS = useMemo(() => [t('heading.confirmed'), t('heading.deaths'), t('heading.recovered')], [t]);

    const handleReset = useCallback(() => {
      selectCountry({
        name: null,
        code: null,
        population: 0,
      });
      changeSelectedPeriod(false);
      changeIsFor100(false);
      changeShowingData(constants.DEFAULT_DATA);
    });

    const changeLanguage = (lng) => {
      i18n.changeLanguage(lng);
    };

    const DROPDOWN_TITLES = useMemo(
      () => ({
        options: t('dropdown.titles.options'),
        translations: t('dropdown.titles.language'),
      }),
      [t],
    );

    const renderTableRows = useMemo(() => {
      const data = getTableData(currentCountry.code, responseData);
      const periodData = getDataForPeriodDashboard(isLastDay, data);
      const totalPopulation = getTotalPopulation(currentCountry.code);
      if (isFor100) {
        periodData.confirmed = countFor100(periodData.confirmed, totalPopulation);
        periodData.deaths = countFor100(periodData.deaths, totalPopulation);
        periodData.recovered = countFor100(periodData.recovered, totalPopulation);
      }
      return (
        <tr>
          <td>{periodData.confirmed.toLocaleString('ru')}</td>
          <td>{periodData.deaths.toLocaleString('ru')}</td>
          <td>{periodData.recovered.toLocaleString('ru')}</td>
        </tr>
      );
    }, [currentCountry.code, responseData, isLastDay, isFor100]);

    return (
      <div className={isFullScreenSize ? 'dashboard-table full-container' : 'dashboard-table'}>
        <div className="table-wrapper">
          <ExpandBtn setIsFullScreenSize={setIsFullScreenSize} isFullScreenSize={isFullScreenSize} />
          <div className="settings">
            <Button className="reset-btn" variant="info" onClick={handleReset}>
              {t('dropdown.titles.reset')}
            </Button>
            <DropdownDisplayOptions
              setCurrShowingData={changeLanguage}
              options={constants.LANGUAGES}
              title={DROPDOWN_TITLES.translations}
              isResizable ={false}
            />
          </div>

          <h1 className="table-header">
            {t('dashboardTable')}
            {'\n'}
          </h1>
          <span>{currentCountry.name ?? t('whole-world-name')}</span>
          <Table>
            <thead>
              <tr>
                {HEADINGS.map((heading) => (
                  <th key={heading}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {responseData ? (
                renderTableRows
              ) : (
                <tr>
                  <td colSpan={HEADINGS.length}>{t('error.error-message')}</td>
                </tr>
              )}
            </tbody>
          </Table>
          <FilterForm />
        </div>
      </div>
    );
  },
);

DashboardTable.propTypes = {
  responseData: PropTypes.arrayOf(PropTypes.object),
  responseDataWorld: PropTypes.objectOf(PropTypes.any),
  currentCountry: PropTypes.objectOf(PropTypes.any).isRequired,
  isFor100: PropTypes.bool.isRequired,
  isLastDay: PropTypes.bool.isRequired,
  selectCountry: PropTypes.func.isRequired,
  changeSelectedPeriod: PropTypes.func.isRequired,
  changeIsFor100: PropTypes.func.isRequired,
};

DashboardTable.defaultProps = {
  responseDataWorld: '',
  responseData: '',
};

export default DashboardTable;
